import { useTranslation } from "react-i18next";

import DeleteRounded from "@mui/icons-material/DeleteRounded";
import DownloadRounded from "@mui/icons-material/DownloadRounded";
import CreateCaseNoteIcon from "@mui/icons-material/Inbox";
import PictureAsPdfRounded from "@mui/icons-material/PictureAsPdfRounded";
import RenameIcon from "@mui/icons-material/TitleOutlined";
import VisibilityRounded from "@mui/icons-material/VisibilityRounded";

import { FileActionsMenuItem } from "../FileActionsMenu.types";
import { insertFileActionItems } from "../FileActionsMenu.utils";

interface Params {
  onPreview?: () => void;
  onRename?: () => void;
  onDownload?: () => void;
  onCreateCaseNote?: () => void;
  onConvertToPdf?: () => void;
  onDelete?: () => void;
  /** Row-specific actions, placed via FileActionsMenuItem.index. */
  extraItems?: FileActionsMenuItem[];
}

/**
 * Single source of the standard file actions, so every file row shows the same labels, icons and
 * order: preview, rename, download, create case note, convert to PDF, and delete last. An action is only listed when
 * its handler is supplied; rows that need more than the standard set pass those as extraItems instead
 * of redeclaring the standard ones.
 */
export function useFileActionItems({
  onPreview,
  onRename,
  onDownload,
  onCreateCaseNote,
  onConvertToPdf,
  onDelete,
  extraItems,
}: Params): FileActionsMenuItem[] {
  const { t } = useTranslation();

  const standardItems: FileActionsMenuItem[] = [];
  if (onPreview !== undefined) {
    standardItems.push({
      key: "preview",
      label: t("common.fileActions.preview"),
      icon: <VisibilityRounded />,
      onClick: onPreview,
    });
  }
  if (onRename !== undefined) {
    standardItems.push({
      key: "rename",
      label: t("common.fileActions.rename"),
      icon: <RenameIcon />,
      onClick: onRename,
    });
  }
  if (onDownload !== undefined) {
    standardItems.push({
      key: "download",
      label: t("common.fileActions.download"),
      icon: <DownloadRounded />,
      onClick: onDownload,
    });
  }
  if (onCreateCaseNote !== undefined) {
    standardItems.push({
      key: "create-case-note",
      label: t("common.fileActions.createCaseNote"),
      icon: <CreateCaseNoteIcon />,
      onClick: onCreateCaseNote,
    });
  }
  if (onConvertToPdf !== undefined) {
    standardItems.push({
      key: "convert-to-pdf",
      label: t("common.fileActions.convertToPdf"),
      icon: <PictureAsPdfRounded />,
      onClick: onConvertToPdf,
    });
  }

  const items = insertFileActionItems(standardItems, extraItems ?? []);

  // Delete is appended after the row-specific actions are placed, which keeps it last in every menu.
  if (onDelete !== undefined) {
    items.push({
      key: "delete",
      label: t("common.fileActions.delete"),
      icon: <DeleteRounded />,
      onClick: onDelete,
    });
  }

  return items;
}
